import { memo, useMemo, useState } from 'react';
import { isEmpty, map, take } from 'lodash';
import {
  Grid, Typography, Stack, Divider,
} from '@mui/material';
import { useFirestore } from '@client/hooks/utils.hooks';
import { BaseCard } from '@client/components/common/BaseCard';
import { upsertShows } from '@client/slices/entities/shows.reducer';
import { getDateTime } from '@sda/utils/date';
import { SearchLoadMore } from '@client/components/common/SearchLoadMore';
import { SearchToggleButton } from '@client/components/common/SearchToggleButton';
import { SoldOutIndicator } from '@client/components/common/SoldOutIndicator';
import { getEventLocation } from '@sda/utils/string';

interface ShowsProps {
  readonly profileId: string;
  readonly displayPast?: boolean;
}

export const Shows = memo<ShowsProps>(({ profileId, displayPast = false }) => {
  const [limit, setLimit] = useState(10);
  const [displayHiddenShows, setDisplayHiddenShows] = useState(false);

  const [shows, showsLoading] = useFirestore((SDK) => SDK.Shows.listBy((query) => [
    profileId && query.contains('profileIds', profileId),
    query.isEqualTo('finished', displayPast),
    query.isTrue('isHyperwalletShow'),
    query.isFalse('isIncomplete'),
    query.isFalse('isCancelled'),
    query.isTrue('hasPublicTickets'),
    query.isFalse('hasExternalTickets'),
    query.isLessThanOrEqualTo('announceTime', new Date()),
  ], { limit }), { key: `${profileId}${limit}`, upserter: upsertShows });

  const showArrays = useMemo(() => {
    if (isEmpty(shows)) {
      return null;
    }

    const showArray = Object.values(shows);

    const firstArray = take(showArray, 2);
    const secondArray = showArray.slice(2);

    return [firstArray, secondArray];
  }, [shows]);

  if (isEmpty(showArrays)) {
    return null;
  }

  const [openShows, hiddenShows] = showArrays;

  return (
    <>
      <div>
        <Divider variant="extended" />
      </div>

      <Stack spacing={3}>
        <Typography variant="subtitle2">
          {displayPast && 'Past '}

          Shows
        </Typography>

        <div>
          <Grid
            container
            spacing={3}
          >
            {map(openShows, (show) => {
              let location = getEventLocation(show);

              if (show.isOnlineOnly) {
                location = 'Online';
              }

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={show.id}
                >
                  <BaseCard
                    imgSrc={show.publicImageUrl}
                    title={show.title}
                    description={`${location}, ${getDateTime(show.startTime).toFormat('MMM d')}`}
                    path={`/shows/${show.id}`}
                    chipComponent={(
                      <SoldOutIndicator showId={show.id} />
                    )}
                  />
                </Grid>
              );
            })}
          </Grid>
        </div>

        {!isEmpty(hiddenShows) && (
          <SearchToggleButton
            selected={displayHiddenShows}
            onChange={() => setDisplayHiddenShows(!displayHiddenShows)}
          >
            View More
          </SearchToggleButton>
        )}

        {Boolean(displayHiddenShows) && !isEmpty(hiddenShows) && (
          <>
            <div>
              <Grid
                container
                spacing={3}
              >
                {map(hiddenShows, (show) => {
                  let location = getEventLocation(show);

                  if (show.isOnlineOnly) {
                    location = 'Online';
                  }

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      key={show.id}
                    >
                      <BaseCard
                        imgSrc={show.publicImageUrl}
                        title={show.title}
                        description={`${location}, ${getDateTime(show.startTime).toFormat('MMM d')}`}
                        path={`/shows/${show.id}`}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </div>

            {!showsLoading && Object.values(shows).length >= limit && (
              <SearchLoadMore
                onClick={() => setLimit(limit + 10)}
              />
            )}
          </>
        )}
      </Stack>
    </>
  );
});
